import React, { useState, useEffect, useMemo } from 'react';
import { apiService, LinkPerksBusinessResponse, Business } from '../services/api';
import './SuperAdminViews.css';

type BusinessSubTab = 'vendor' | 'employee';

const AllBusinessesView: React.FC = () => {
  const [vendorBusinesses, setVendorBusinesses] = useState<LinkPerksBusinessResponse[]>([]);
  const [allPublicBusinesses, setAllPublicBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<BusinessSubTab>('vendor');
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<{ id: number; name: string; image?: string; category?: string } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      setError(null);
      const [nearbyData, publicData] = await Promise.all([
        apiService.getAllBusinessesNearby(),
        apiService.getAllPublicBusinesses(),
      ]);
      setVendorBusinesses(Array.isArray(nearbyData) ? nearbyData : []);
      setAllPublicBusinesses(Array.isArray(publicData) ? publicData : []);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch businesses');
    } finally {
      setLoading(false);
    }
  };

  const employeePartners = useMemo(() => {
    const vendorIds = new Set(vendorBusinesses.map(b => b.id));
    return allPublicBusinesses.filter(b => !vendorIds.has(b.id));
  }, [vendorBusinesses, allPublicBusinesses]);

  const filteredVendors = useMemo(() => {
    return vendorBusinesses.filter(business => {
      const term = searchTerm.toLowerCase();
      return (
        business.name.toLowerCase().includes(term) ||
        (business.category?.toLowerCase().includes(term)) ||
        (business.city?.toLowerCase().includes(term)) ||
        (business.description?.toLowerCase().includes(term))
      );
    });
  }, [vendorBusinesses, searchTerm]);

  const filteredEmployees = useMemo(() => {
    return employeePartners.filter(business => {
      const term = searchTerm.toLowerCase();
      return (
        business.name.toLowerCase().includes(term) ||
        (business.description?.toLowerCase().includes(term))
      );
    });
  }, [employeePartners, searchTerm]);

  const openDeleteModal = (business: { id: number; name: string; image?: string; category?: string }) => {
    setBusinessToDelete(business);
    setDeleteConfirmText('');
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setBusinessToDelete(null);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const handleDeleteBusiness = async () => {
    if (!businessToDelete || deleteConfirmText.toLowerCase() !== 'delete') {
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteError(null);
      await apiService.deleteSuperAdminBusiness(businessToDelete.id);
      
      setVendorBusinesses(prev => prev.filter(b => b.id !== businessToDelete.id));
      setAllPublicBusinesses(prev => prev.filter(b => b.id !== businessToDelete.id));
      closeDeleteModal();
    } catch (err) {
      console.error('Error deleting business:', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete business');
    } finally {
      setIsDeleting(false);
    }
  };

  const getCategoryDisplay = (category?: string) => {
    if (category) {
      return <span className="type-badge">{category}</span>;
    }
    return <span style={{ color: '#a0aec0' }}>—</span>;
  };

  if (loading) {
    return (
      <div className="admin-view">
        <div className="loading-state">
          <p>Loading businesses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-view">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchBusinesses} className="add-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-view">
      <div className="view-header">
        <div>
          <h2>All Businesses</h2>
          <p className="view-description">View all businesses on the platform</p>
        </div>
        <button onClick={fetchBusinesses} className="add-button">
          🔄 Refresh
        </button>
      </div>

      <div className="business-sub-tabs">
        <button
          className={`business-sub-tab ${activeSubTab === 'vendor' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('vendor')}
        >
          Vendor Partners
          <span className="sub-tab-count">{vendorBusinesses.length}</span>
        </button>
        <button
          className={`business-sub-tab ${activeSubTab === 'employee' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('employee')}
        >
          Participating Employers
          <span className="sub-tab-count">{employeePartners.length}</span>
        </button>
      </div>

      {activeSubTab === 'vendor' && (
        <div className="sub-tab-header vendor">
          <div className="sub-tab-header-icon">🏪</div>
          <div>
            <strong>Vendor Partners</strong>
            <p>These businesses have active offers and locations that show up on the LinkPerks app.</p>
          </div>
        </div>
      )}

      {activeSubTab === 'employee' && (
        <div className="sub-tab-header employee">
          <div className="sub-tab-header-icon">👥</div>
          <div>
            <strong>Participating Employers</strong>
            <p>These businesses don't have offerings listed on LinkPerks but allow their employees to set up accounts.</p>
          </div>
        </div>
      )}

      <div className="view-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, category, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {activeSubTab === 'vendor' && (
        <>
          {filteredVendors.length === 0 ? (
            <div className="empty-state">
              <p>No vendor partner businesses found matching your criteria.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Business Name</th>
                    <th>Category</th>
                    <th>Locations</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVendors.map(business => (
                    <tr key={business.id}>
                      <td>
                        <div className="name-cell">
                          {business.image ? (
                            <img 
                              src={business.image} 
                              alt={business.name} 
                              className="avatar-img"
                              style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="avatar">
                              {business.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <strong>{business.name}</strong>
                            {business.description && (
                              <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                {business.description.substring(0, 50)}{business.description.length > 50 ? '...' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{getCategoryDisplay(business.category)}</td>
                      <td className="text-center">{business.locations?.length || 0}</td>
                      <td>
                        <button 
                          className="delete-btn"
                          onClick={() => openDeleteModal(business)}
                          title="Delete business"
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeSubTab === 'employee' && (
        <>
          {filteredEmployees.length === 0 ? (
            <div className="empty-state">
              <p>No employee partner businesses found matching your criteria.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Business Name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(business => (
                    <tr key={business.id}>
                      <td>
                        <div className="name-cell">
                          {business.image ? (
                            <img 
                              src={business.image} 
                              alt={business.name} 
                              className="avatar-img"
                              style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="avatar">
                              {business.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <strong>{business.name}</strong>
                            {business.description && (
                              <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                {business.description.substring(0, 50)}{business.description.length > 50 ? '...' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <button 
                          className="delete-btn"
                          onClick={() => openDeleteModal(business)}
                          title="Delete business"
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && businessToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h3>⚠️ Delete Business</h3>
              <button className="modal-close" onClick={closeDeleteModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="delete-warning">
                <div className="warning-icon">🚨</div>
                <h4>This action is non-reversible!</h4>
                <p>You are about to permanently delete:</p>
                <div className="business-to-delete">
                  {businessToDelete.image ? (
                    <img 
                      src={businessToDelete.image} 
                      alt={businessToDelete.name}
                      className="delete-business-img"
                    />
                  ) : (
                    <div className="delete-business-avatar">
                      {businessToDelete.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <strong>{businessToDelete.name}</strong>
                    {businessToDelete.category && (
                      <span className="delete-business-category">{businessToDelete.category}</span>
                    )}
                  </div>
                </div>
                <p className="delete-consequences">
                  This will permanently delete the business, all its locations, offerings, and redemption history. 
                  This cannot be undone.
                </p>
              </div>

              <div className="delete-confirm-input">
                <label htmlFor="deleteConfirm">
                  Type <strong>delete</strong> below to confirm:
                </label>
                <input
                  id="deleteConfirm"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type 'delete' to confirm"
                  className="delete-input"
                  autoComplete="off"
                />
              </div>

              {deleteError && (
                <div className="delete-error">
                  ❌ {deleteError}
                </div>
              )}
            </div>

            <div className="modal-footer delete-footer">
              <button 
                className="cancel-btn" 
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={handleDeleteBusiness}
                disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeleting}
              >
                {isDeleting ? 'Deleting...' : '🗑️ Delete Business'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllBusinessesView;
